import { FC, memo, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import * as Icon from 'react-feather';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { colorAccent1, colorPrimary } from '@atlas-frontend/design-tokens';
import {
  formatDate,
  formatDateTimeShort,
  getMapBy,
  getUserName,
  INPUT_DATA_EVENT_LEVEL_CODES,
  INPUT_DATA_PROCESSING_TYPE_CODES,
  InputProcessingErrorExcelUrl,
  ProcessingEventDirectoryEntryType,
  Table,
  TableOperationButton,
  TableProps,
  TextEllipsis,
  Tooltip,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import { withParams } from '../../../paths/withParams';
import translations from './InputDataProcessingEventsTable.translations.json';
import '../table/filterInputs';
import {
  ToggleInputDataProcessingEventStatusSwitch,
  ToggleProcessingEventStatusProps,
} from './ToggleInputDataProcessingEventStatusSwitch';
import { expand } from 'uri-template-lite';

const { addTranslations, useTranslation } = i18nNsFactory(
  'processing-errors-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<ProcessingEventDirectoryEntryType>();

export interface InputDataProcessingEventsTableProps
  extends Omit<
    TableProps<ProcessingEventDirectoryEntryType>,
    'name' | 'columns' | 'scrollContainer' | 'virtual'
  > {
  toggleStatus: ToggleProcessingEventStatusProps;
}

export const InputDataProcessingEventsTable: FC<
  InputDataProcessingEventsTableProps
> = (props) => {
  const { t } = useTranslation();
  const { toggleStatus, ...rest } = props;

  const {
    PROCESSING_TYPE: processingTypeOptions,
    PROCESSING_ERROR_LEVEL: errorLevelOptions,
    PROCESSING_STATUS: statusOptions,
  } = useSystemClassifierSelectOptions(
    ['PROCESSING_TYPE', 'PROCESSING_ERROR_LEVEL', 'PROCESSING_STATUS'],
    ({ id, name, code }) => ({ id, name, code }),
  );

  const filterOptions = useMemo(() => {
    const forTable = {
      processingTypeId: processingTypeOptions.filter(({ code }) =>
        INPUT_DATA_PROCESSING_TYPE_CODES.includes(code),
      ),
      errorLevelId: errorLevelOptions.filter(({ code }) =>
        INPUT_DATA_EVENT_LEVEL_CODES.includes(code),
      ),
      statusId: statusOptions,
    };

    return {
      forTable,
      byId: {
        processingTypeId: getMapBy(forTable.processingTypeId, 'id'),
        errorLevelId: getMapBy(forTable.errorLevelId, 'id'),
        statusId: getMapBy(forTable.statusId, 'id'),
      },
    };
  }, [processingTypeOptions, errorLevelOptions, statusOptions]);

  const isError = useCallback(
    (rowData: ProcessingEventDirectoryEntryType) => {
      const errorLevel = filterOptions.byId.errorLevelId.get(
        rowData.errorLevelId || 0,
      );
      return errorLevel?.code === 'ERROR';
    },
    [filterOptions.byId.errorLevelId],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor(
        ({ processingTypeId }) =>
          filterOptions.byId.processingTypeId.get(processingTypeId)?.name || '',
        {
          id: 'processingTypeId',
          header: t('header.processingTypeId'),
          filterFn: 'includesOption',
          meta: {
            className: 'sticky-left',
            width: '20ch',
          },
        },
      ),
      columnHelper.accessor('dataObjectCodeName', {
        id: 'dataObjectCodeName',
        header: t('header.dataObjectCodeName'),
        filterFn: 'inputSampleSelect',
        cell: SampleCell,
        meta: {
          className: 'sticky-left',
          width: '25ch',
        },
      }),
      columnHelper.accessor(({ valueDate }) => formatDate(valueDate), {
        id: 'valueDate',
        header: t('header.valueDate'),
        filterFn: 'valueDateSelect',
        meta: { width: '18ch' },
      }),
      columnHelper.accessor(({ startTime }) => formatDateTimeShort(startTime), {
        id: 'startTime',
        header: t('header.startTime'),
        filterFn: 'validPeriod',
        meta: { width: '21ch' },
      }),
      columnHelper.accessor(({ endTime }) => formatDateTimeShort(endTime), {
        id: 'endTime',
        header: t('header.endTime'),
        filterFn: 'validPeriod',
        meta: { width: '21ch' },
      }),
      columnHelper.accessor(({ createdBy }) => createdBy.username, {
        id: 'createdBy',
        header: t('header.createdBy'),
        cell: CreatedByCell,
        meta: { width: '15ch' },
      }),
      columnHelper.accessor(
        ({ errorLevelId }) =>
          filterOptions.byId.errorLevelId.get(errorLevelId || 0)?.name || '',
        {
          id: 'errorLevelId',
          header: t('header.errorLevelId'),
          cell: (props) => (
            <ErrorLevelCell
              isError={isError(props.row.original)}
              hasAccess={props.row.original.hasAccess}
              {...props}
            />
          ),
          filterFn: 'includesOption',
          meta: { width: '15ch' },
        },
      ),
      columnHelper.accessor(
        ({ statusId }) =>
          (statusId && filterOptions.byId.statusId.get(statusId)?.name) || '',
        {
          id: 'statusId',
          header: t('header.statusId'),
          cell: (props) => (
            <StatusCell
              statusCode={
                props.row.original.statusId
                  ? filterOptions.byId.statusId.get(props.row.original.statusId)
                      ?.code
                  : undefined
              }
              onToggleStatus={toggleStatus.onToggle}
              isLoading={toggleStatus.idInLimbo === props.row.original.id}
              isFlipped={toggleStatus.idFlipped === props.row.original.id}
              isDisabled={
                !props.row.original.permissionToManage ||
                !isError(props.row.original)
              }
              {...props}
            />
          ),
          filterFn: 'includesOption',
          meta: { width: '15ch' },
        },
      ),
      columnHelper.display({
        id: 'operation',
        header: t('header.operations'),
        cell: (props) => (
          <OperationsCell {...props} hasAccess={props.row.original.hasAccess} />
        ),
        meta: { width: '8ch' },
        enableColumnFilter: false,
      }),
    ];
  }, [t, filterOptions, toggleStatus, isError]);

  return (
    <Table
      name="processing-errors-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      options={filterOptions.forTable}
      scrollContainerSelector="#processing-errors-directory-content"
      sticky
      virtual
      {...rest}
    />
  );
};

const SampleCell: FC<CellContext<ProcessingEventDirectoryEntryType, any>> =
  memo((props) => {
    const { row } = props;
    const { id, dataObjectId, valueDate, dataObjectType } = row.original;

    return (
      <TextEllipsis>
        <UiLink
          id={`sampleReference-${id}`}
          path={path.sampleView}
          params={{
            id: dataObjectId,
            date: valueDate,
            type: dataObjectType,
          }}
        >
          {props.renderValue()}
        </UiLink>
      </TextEllipsis>
    );
  });

const CreatedByCell: FC<CellContext<ProcessingEventDirectoryEntryType, any>> =
  memo((props) => {
    const { createdBy } = props.row.original;
    return (
      <Tooltip title={getUserName(createdBy)}>{createdBy.username}</Tooltip>
    );
  });

const ErrorLevelCell: FC<
  CellContext<ProcessingEventDirectoryEntryType, any> & {
    isError: boolean;
    hasAccess: boolean;
  }
> = memo((props) => {
  const { row, isError, hasAccess } = props;
  const { t } = useTranslation();

  return (
    <TableOperationButton
      title={hasAccess ? t('button.open-error-report') : t('button.no-access')}
      id={`open-error-report-${row.id}`}
      type="default"
      icon={
        <Icon.AlertTriangle
          size={18}
          color={isError ? colorAccent1 : colorPrimary}
        />
      }
      disabled={!hasAccess}
      href={withParams(path.inputDataErrorReport, {
        eventId: props.row.original.id,
      })}
      target="_blank"
      rel="opener"
    />
  );
});

const StatusCell: FC<
  CellContext<ProcessingEventDirectoryEntryType, any> & {
    statusCode?: string;
    onToggleStatus: (
      processingEventId: number,
      isResolved: boolean,
    ) => Promise<unknown>;
    isLoading: boolean;
    isFlipped: boolean;
    isDisabled: boolean;
  }
> = memo((props) => {
  const { row, statusCode, ...rest } = props;

  if (!row.original.permissionToManage) {
    return null;
  }

  return (
    <ToggleInputDataProcessingEventStatusSwitch
      id={`toggle-error-status-${row.id}`}
      processingEventId={row.original.id}
      isResolved={statusCode === 'RESOLVED'}
      {...rest}
    />
  );
});

const OperationsCell: FC<
  CellContext<ProcessingEventDirectoryEntryType, any> & {
    hasAccess: boolean;
  }
> = memo((props) => {
  const { t } = useTranslation();
  const { row, hasAccess } = props;
  const { id, dataObjectId, valueDate, dataObjectType } = row.original;

  return (
    <Flex gap="small" justify="space-between">
      <Flex gap="small">
        <TableOperationButton
          id={`download-${id}`}
          title={hasAccess ? t('button.download') : t('button.no-access')}
          icon={<Icon.Download size={18} />}
          href={expand(InputProcessingErrorExcelUrl, { id })}
          disabled={!hasAccess}
        />
        <UiLink
          id={`open-${id}`}
          path={path.inputDataView}
          params={{
            id: dataObjectId,
            date: valueDate,
            type: dataObjectType,
          }}
        >
          {({ type, ...props }) => (
            <TableOperationButton
              {...props}
              title={t('button.open-data')}
              icon={<Icon.FileText size={18} />}
            />
          )}
        </UiLink>
      </Flex>
    </Flex>
  );
});
